/** Document-stage rich rendering. Split out of StageRichBody (CC-10). */
import { FileText, Languages, ScanLine } from 'lucide-react';
import { Stat } from './StageRichBits';
import { TranslateToggle } from './TranslateToggle';
import { ConfidenceBadge } from './ConfidenceBadge';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asNumber = (v: unknown): number => (typeof v === 'number' ? v : 0);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

export function RichDocumentBody({
  output,
  jobId,
  onTranslated,
}: {
  output: Record<string, unknown>;
  jobId?: string | null;
  onTranslated?: () => void;
}) {
  const text = asText(output.full_text);
  const excerpt = text.length > 800 ? `${text.slice(0, 800)}…` : text;
  const term = obj(output.contract_term);
  const fin = obj(output.financial_indicators);
  const summary = asText(output.contract_summary);
  const govLaw = asText(output.governing_law);
  const dispute = asText(output.dispute_resolution);
  const score = output.document_confidence_score;
  const flags = asArray(output.flags_for_downstream).map((x) => (typeof x === 'string' ? x : ''));
  const notes = asText(output.extraction_notes);
  const partiesMentioned = asArray(output.parties_mentioned);
  const keyDates = asArray(output.key_dates);
  const attachments = asArray(output.attachments_referenced).map((x) =>
    typeof x === 'string' ? x : '',
  );
  const sections = asArray(output.sections);
  const secLangs = asArray(output.secondary_languages).map((x) =>
    typeof x === 'string' ? x : '',
  );

  return (
    <div className="space-y-3">
      <ConfidenceBadge score={score} rationale={notes} />
      {summary && (
        <div className="rounded border bg-muted/30 p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Contract summary
          </p>
          <p>{summary}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <Stat icon={FileText} label="Document type" value={asText(output.document_type) || '—'} />
        <Stat icon={Languages} label="Language" value={asText(output.language) || '—'} />
        <Stat icon={ScanLine} label="Pages" value={String(asNumber(output.page_count))} />
        <Stat icon={FileText} label="Governing law" value={govLaw || '—'} />
        <Stat icon={FileText} label="Dispute resolution" value={dispute || '—'} />
      </div>
      {secLangs.length > 0 && (
        <p className="text-xs text-muted-foreground">Also detected: {secLangs.join(', ')}</p>
      )}
      {(asText(term.start_date) || asText(term.end_date) || asText(term.duration_stated)) && (
        <div className="rounded border p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Contract term
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div>Start: {asText(term.start_date) || '—'}</div>
            <div>End: {asText(term.end_date) || '—'}</div>
            <div>Duration: {asText(term.duration_stated) || '—'}</div>
            <div>
              Auto-renewal:{' '}
              {term.auto_renewal === true ? 'yes' : term.auto_renewal === false ? 'no' : '—'}
            </div>
          </div>
        </div>
      )}
      {(asText(fin.total_contract_value) ||
        asText(fin.payment_frequency) ||
        asArray(fin.currencies_mentioned).length > 0) && (
        <div className="rounded border p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Financial indicators
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div>Value: {asText(fin.total_contract_value) || '—'}</div>
            <div>Frequency: {asText(fin.payment_frequency) || '—'}</div>
            <div>
              Currencies:{' '}
              {asArray(fin.currencies_mentioned)
                .map((c) => (typeof c === 'string' ? c : ''))
                .filter(Boolean)
                .join(', ') || '—'}
            </div>
            <div>Penalty mentioned: {fin.penalty_or_late_fee_mentioned ? 'yes' : 'no'}</div>
          </div>
        </div>
      )}
      {partiesMentioned.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Parties mentioned ({partiesMentioned.length})
          </p>
          <ul className="space-y-1 text-xs">
            {partiesMentioned.map((p, i) => {
              const o = obj(p);
              return (
                <li key={i} className="rounded border px-2 py-1">
                  <span className="font-medium">{asText(o.raw_name) || '—'}</span>
                  {asText(o.role_in_contract) && (
                    <span className="text-muted-foreground"> · {asText(o.role_in_contract)}</span>
                  )}
                  {asText(o.entity_type_hint) && (
                    <span className="text-muted-foreground"> · {asText(o.entity_type_hint)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {keyDates.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Key dates ({keyDates.length})
          </p>
          <ul className="space-y-1 text-xs">
            {keyDates.map((d, i) => {
              const o = obj(d);
              return (
                <li key={i} className="flex justify-between gap-3 border-b py-0.5">
                  <span>{asText(o.label) || '—'}</span>
                  <span className="font-medium">{asText(o.value) || '—'}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {sections.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Document sections ({sections.length})
          </summary>
          <ul className="mt-1 space-y-1 text-xs">
            {sections.slice(0, 30).map((s, i) => {
              const o = obj(s);
              return (
                <li key={i} className="border-b py-0.5">
                  <span className="font-medium">
                    {asText(o.section_number)} {asText(o.title)}
                  </span>
                  {asText(o.summary) && (
                    <div className="text-muted-foreground">{asText(o.summary)}</div>
                  )}
                </li>
              );
            })}
            {sections.length > 30 && (
              <li className="text-muted-foreground italic">… {sections.length - 30} more</li>
            )}
          </ul>
        </details>
      )}
      {attachments.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Attachments referenced: {attachments.join(' · ')}
        </p>
      )}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((f, i) => (
            <span
              key={i}
              className="text-[10px] rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}
      {notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
          Extraction notes: {notes}
        </p>
      )}
      {excerpt && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Extracted text (first 800 chars) + translate
          </summary>
          <div className="mt-1">
            <TranslateToggle
              text={excerpt}
              sourceLanguage={asText(output.language) || null}
              jobId={jobId ?? null}
              onTranslated={onTranslated}
            />
          </div>
        </details>
      )}
    </div>
  );
}
